import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/content";
import { Lock, LogOut, Users, Newspaper, Trash2, ArrowLeft, Upload, ImageIcon, Pencil, X, Download, Mail } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "age_token";

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const inputCls = "w-full bg-white border border-foreground/15 rounded-xl px-4 py-3 text-sm font-medium outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/60";

function Login({ onLogin }) {
  const { t } = useLang();
  const a = t.admin;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
      toast.success("Benvenuto/a, " + (data.user.name || "Direttivo"));
    } catch {
      toast.error(a.loginError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-14">
        <Link to="/" className="flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-primary-foreground">
          <ArrowLeft size={16} /> {a.backHome}
        </Link>
        <div>
          <img src={IMAGES.logo} alt="AGE Rende APS" className="h-20 w-20 rounded-full bg-white p-1 mb-8" />
          <h1 className="font-display font-extrabold text-5xl tracking-tight leading-[0.95] mb-4">AGE Rende<br />APS</h1>
          <p className="font-serif-ed italic text-2xl text-primary-foreground/80">La voce dei genitori, il futuro dei nostri figli.</p>
        </div>
        <p className="text-xs text-primary-foreground/50">Comunità Educante · Rende (CS)</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-14 bg-background">
        <form data-testid="login-form" onSubmit={submit} className="w-full max-w-md">
          <span className="inline-grid place-items-center w-12 h-12 rounded-xl bg-primary text-primary-foreground mb-6"><Lock size={20} /></span>
          <h2 className="font-display font-extrabold text-3xl tracking-tight mb-1">{a.loginTitle}</h2>
          <p className="text-muted-foreground mb-8">{a.loginSub}</p>
          <div className="space-y-4">
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={a.email} className={inputCls} />
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={a.password} className={inputCls} />
            <button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground font-semibold rounded-full py-3.5 hover:bg-[hsl(214,76%,24%)] active:scale-[0.98] transition-all disabled:opacity-60">
              {loading ? "..." : a.login}
            </button>
          </div>
          <Link to="/" className="lg:hidden mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground"><ArrowLeft size={16} /> {a.backHome}</Link>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const { t, lang } = useLang();
  const a = t.admin;
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [news, setNews] = useState([]);
  const emptyForm = { titolo_it: "", titolo_en: "", testo_it: "", testo_en: "", data: new Date().toISOString().slice(0, 10), categoria: "Evento", images: [] };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [exportingSubs, setExportingSubs] = useState(false);
  const fileRef = useRef(null);

  const loadMembers = () => axios.get(`${API}/members`, authHeader()).then((r) => setMembers(r.data)).catch(() => {});
  const loadNews = () => axios.get(`${API}/news`).then((r) => setNews(r.data)).catch(() => {});
  const loadSubscribers = () => axios.get(`${API}/subscribers`, authHeader()).then((r) => setSubscribers(r.data)).catch(() => {});

  useEffect(() => { loadMembers(); loadNews(); loadSubscribers(); }, []);

  const exportSubscribers = async () => {
    setExportingSubs(true);
    try {
      const res = await axios.get(`${API}/subscribers/export`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "iscritti_newsletter.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore esportazione");
    } finally {
      setExportingSubs(false);
    }
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, { headers: { ...authHeader().headers } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(files.length > 1 ? "Foto caricate" : "Foto caricata");
    } catch {
      toast.error("Errore caricamento foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      titolo_it: item.titolo_it || "", titolo_en: item.titolo_en || "",
      testo_it: item.testo_it || "", testo_en: item.testo_en || "",
      data: (item.data || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      categoria: item.categoria || "Evento",
      images: item.images && item.images.length ? item.images : (item.image_url ? [item.image_url] : []),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); if (fileRef.current) fileRef.current.value = ""; };

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      if (editingId) {
        await axios.put(`${API}/news/${editingId}`, form, authHeader());
        toast.success("News aggiornata");
      } else {
        await axios.post(`${API}/news`, form, authHeader());
        toast.success("News pubblicata");
      }
      cancelEdit();
      loadNews();
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setPublishing(false);
    }
  };

  const removeNews = async (id) => {
    try {
      await axios.delete(`${API}/news/${id}`, authHeader());
      toast.success("News eliminata");
      loadNews();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/members/${id}`, { status }, authHeader());
      setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, status } : m)));
    } catch {
      toast.error("Errore aggiornamento stato");
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/members/export`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "soci_age_rende.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore esportazione");
    } finally {
      setExporting(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="mx-auto max-w-[1200px] px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="" className="h-9 w-9 rounded-full" />
            <span className="font-display font-extrabold tracking-tight">{a.dashTitle}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground hidden sm:block">{a.backHome}</Link>
            <button data-testid="logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 text-sm font-semibold border border-foreground/20 rounded-full px-4 py-2 hover:bg-foreground hover:text-background transition-colors">
              <LogOut size={15} /> {a.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-5 md:px-8 py-10">
        <div className="flex gap-2 mb-8">
          <button data-testid="tab-members" onClick={() => setTab("members")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "members" ? "bg-primary text-primary-foreground" : "border border-foreground/15 hover:bg-secondary"}`}>
            <Users size={16} /> {a.tabMembers} <span className="ml-1 opacity-70">({members.length})</span>
          </button>
          <button data-testid="tab-news" onClick={() => setTab("news")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "news" ? "bg-primary text-primary-foreground" : "border border-foreground/15 hover:bg-secondary"}`}>
            <Newspaper size={16} /> {a.tabNews} <span className="ml-1 opacity-70">({news.length})</span>
          </button>
          <button data-testid="tab-subscribers" onClick={() => setTab("subscribers")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === "subscribers" ? "bg-primary text-primary-foreground" : "border border-foreground/15 hover:bg-secondary"}`}>
            <Mail size={16} /> {a.tabSubscribers} <span className="ml-1 opacity-70">({subscribers.length})</span>
          </button>
        </div>

        {tab === "members" && (
          <div>
            <div className="flex justify-end mb-4">
              <button data-testid="export-btn" onClick={exportExcel} disabled={exporting || members.length === 0} className="inline-flex items-center gap-2 text-sm font-semibold bg-accent text-accent-foreground rounded-full px-5 py-2.5 hover:brightness-95 active:scale-95 transition-all disabled:opacity-50">
                <Download size={16} /> {exporting ? "..." : a.export}
              </button>
            </div>
            <div data-testid="members-panel" className="rounded-2xl border border-black/5 bg-surface overflow-hidden">
              {members.length === 0 ? (
                <p className="p-8 text-muted-foreground">{a.noMembers}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary text-left">
                      <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-5 py-3">{a.colName}</th>
                        <th className="px-5 py-3">{a.colEmail}</th>
                        <th className="px-5 py-3">{a.colPhone}</th>
                        <th className="px-5 py-3">{a.colCity}</th>
                        <th className="px-5 py-3">{a.colVol}</th>
                        <th className="px-5 py-3">{a.colStatus}</th>
                        <th className="px-5 py-3">{a.colDate}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => {
                        const st = m.status || "nuovo";
                        const stColor = st === "approvato" ? "text-green-700 border-green-300 bg-green-50" : st === "contattato" ? "text-[hsl(26,90%,40%)] border-accent/40 bg-accent/10" : "text-primary border-primary/30 bg-primary/5";
                        return (
                          <tr key={m.id} className="border-t border-black/5 hover:bg-secondary/40">
                            <td className="px-5 py-3 font-medium">{m.nome} {m.cognome}</td>
                            <td className="px-5 py-3"><a className="text-primary hover:underline" href={`mailto:${m.email}`}>{m.email}</a></td>
                            <td className="px-5 py-3">{m.telefono || "-"}</td>
                            <td className="px-5 py-3">{m.citta || "-"}</td>
                            <td className="px-5 py-3">{m.volontariato ? "Sì" : "-"}</td>
                            <td className="px-5 py-3">
                              <select data-testid={`status-${m.id}`} value={st} onChange={(e) => changeStatus(m.id, e.target.value)} className={`text-xs font-semibold rounded-full border px-3 py-1.5 outline-none cursor-pointer ${stColor}`}>
                                <option value="nuovo">{a.statuses.nuovo}</option>
                                <option value="contattato">{a.statuses.contattato}</option>
                                <option value="approvato">{a.statuses.approvato}</option>
                              </select>
                            </td>
                            <td className="px-5 py-3 text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleDateString("it-IT") : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "news" && (
          <div data-testid="news-panel" className="grid lg:grid-cols-2 gap-8">
            <form data-testid="news-form" onSubmit={publish} className="rounded-2xl border border-black/5 bg-surface p-6 md:p-8 h-fit">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display font-bold text-xl">{editingId ? a.editLabel : a.newsForm}</h3>
                {editingId && (
                  <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                    <X size={14} /> {a.cancel}
                  </button>
                )}
              </div>
              <div className="grid gap-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="news-titolo-it" required value={form.titolo_it} onChange={set("titolo_it")} placeholder={a.f.titolo_it} className={inputCls} />
                  <input data-testid="news-titolo-en" value={form.titolo_en} onChange={set("titolo_en")} placeholder={a.f.titolo_en} className={inputCls} />
                </div>
                <textarea data-testid="news-testo-it" required rows={3} value={form.testo_it} onChange={set("testo_it")} placeholder={a.f.testo_it} className={`${inputCls} resize-none`} />
                <textarea data-testid="news-testo-en" rows={3} value={form.testo_en} onChange={set("testo_en")} placeholder={a.f.testo_en} className={`${inputCls} resize-none`} />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="news-data" type="date" required value={form.data} onChange={set("data")} className={inputCls} />
                  <select data-testid="news-categoria" value={form.categoria} onChange={set("categoria")} className={inputCls}>
                    <option>Evento</option>
                    <option>Avviso</option>
                    <option>Incontro</option>
                    <option>Progetto</option>
                  </select>
                </div>

                <div>
                  <input ref={fileRef} data-testid="news-image" type="file" accept="image/*" multiple onChange={onFile} className="hidden" id="news-image-input" />
                  <label htmlFor="news-image-input" className="flex items-center gap-2 cursor-pointer text-sm font-medium border border-dashed border-foreground/25 rounded-xl px-4 py-3 hover:bg-secondary transition-colors">
                    <Upload size={16} /> {uploading ? a.uploading : a.addPhotos}
                  </label>
                  {form.images.length > 0 && (
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {form.images.map((im, i) => (
                        <div key={i} className="relative group">
                          <img src={im.startsWith("http") ? im : `${BACKEND}${im}`} alt="" className="h-16 w-full rounded-lg object-cover" />
                          <button type="button" data-testid={`remove-image-${i}`} onClick={() => removeImage(i)} className="absolute -top-1.5 -right-1.5 grid place-items-center w-5 h-5 rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity">
                            <X size={12} />
                          </button>
                          {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-black/60 text-white px-1.5 py-0.5 rounded">Cover</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button data-testid="news-publish" type="submit" disabled={publishing || uploading} className="bg-primary text-primary-foreground font-semibold rounded-full py-3 hover:bg-[hsl(214,76%,24%)] active:scale-[0.98] transition-all disabled:opacity-60">
                  {publishing ? a.publishing : (editingId ? a.update : a.publish)}
                </button>
              </div>
            </form>

            <div className="space-y-4">
              {news.length === 0 ? (
                <p className="text-muted-foreground">{a.noNews}</p>
              ) : (
                news.map((item) => {
                  const img = item.image_url ? (item.image_url.startsWith("http") ? item.image_url : `${BACKEND}${item.image_url}`) : null;
                  return (
                    <div key={item.id} data-testid={`admin-news-${item.id}`} className="flex gap-4 rounded-2xl border border-black/5 bg-surface p-4">
                      {img && <img src={img} alt="" className="h-20 w-20 rounded-xl object-cover shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-semibold uppercase tracking-wider bg-accent/15 text-[hsl(26,90%,40%)] px-2 py-0.5 rounded-full">{item.categoria}</span>
                          <span className="text-xs text-muted-foreground">{new Date(item.data).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</span>
                        </div>
                        <h4 className="font-semibold truncate">{item.titolo_it}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.testo_it}</p>
                      </div>
                      <div className="self-start flex gap-2 shrink-0">
                        <button data-testid={`edit-news-${item.id}`} onClick={() => startEdit(item)} className="grid place-items-center w-9 h-9 rounded-full border border-foreground/15 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`delete-news-${item.id}`} onClick={() => removeNews(item.id)} className="grid place-items-center w-9 h-9 rounded-full border border-foreground/15 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div>
            <div className="flex justify-end mb-4">
              <button data-testid="export-subs-btn" onClick={exportSubscribers} disabled={exportingSubs || subscribers.length === 0} className="inline-flex items-center gap-2 text-sm font-semibold bg-accent text-accent-foreground rounded-full px-5 py-2.5 hover:brightness-95 active:scale-95 transition-all disabled:opacity-50">
                <Download size={16} /> {exportingSubs ? "..." : a.exportSubs}
              </button>
            </div>
            <div data-testid="subscribers-panel" className="rounded-2xl border border-black/5 bg-surface overflow-hidden">
              {subscribers.length === 0 ? (
                <p className="p-8 text-muted-foreground">{a.noSubs}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-left">
                    <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-5 py-3">{a.colSubEmail}</th>
                      <th className="px-5 py-3">{a.colSubDate}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.id} className="border-t border-black/5 hover:bg-secondary/40">
                        <td className="px-5 py-3 font-medium"><a className="text-primary hover:underline" href={`mailto:${s.email}`}>{s.email}</a></td>
                        <td className="px-5 py-3 text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleDateString("it-IT") : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, authHeader())
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-background"><div className="animate-pulse text-muted-foreground">…</div></div>;
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
